import { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { useTranslation } from "react-i18next";

import BUCKET_CATALOG from "@plannel/pages/factory-plan/common/data/fp-bucket-options.json";

// 화면이 고르는 두 케이스 — planScope가 아니라 "시간 버킷 포함 여부" 축(fp-report-screen-guide.md §A-3).
export const BUCKET_CASE = {
  WITH_HOUR: "BUCKET_WITH_HOUR",
  WITHOUT_HOUR: "BUCKET_WITHOUT_HOUR",
};

/**
 * old PeriodSearchGroup.jsx 포팅 — Report 화면(§A) 공용 "조회구간" 조회조건.
 * controlled(§A-1b, forwardRef/useImperativeHandle 미사용). §A-3 결정대로 버킷 옵션은 DB 조회가
 * 아니라 `bucketCase` prop(화면 고정값)으로 정적 카탈로그에서 동기 계산 — planScope와 무관.
 *
 * props:
 *  - value: { startDate, endDate, bucketType, isEntirePlanPeriod }
 *  - onChange: (partial) => void
 *  - bucketCase?: BUCKET_CASE.WITH_HOUR | BUCKET_CASE.WITHOUT_HOUR (기본 WITHOUT_HOUR)
 *  - useBucket?: boolean (기본 true) — old useBucket prop 대응, false면 버킷 Select 자체를 숨김
 *  - entireCheckDisabled?: boolean
 *
 * 화면을 넘나드는 로직(버전 선택→startDate/endDate 재계산, searchZone)은 이 컴포넌트가 소유하지
 * 않는다 — 화면이 소유한다(§A-1b). 이 컴포넌트는 받은 value를 그대로 렌더링만 한다.
 */
const PeriodSearchGroup = ({ value, onChange, bucketCase = BUCKET_CASE.WITHOUT_HOUR, useBucket = true, entireCheckDisabled = false }) => {
  const { t } = useTranslation();

  const inputBoxRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const openPopover = () => setPopoverOpen(true);
  const closePopover = () => setPopoverOpen(false);

  useEffect(() => {
    setAnchorEl(inputBoxRef.current);
  }, []);

  const bucketOptions = useMemo(
    () => (BUCKET_CATALOG[bucketCase] ?? BUCKET_CATALOG[BUCKET_CASE.WITHOUT_HOUR]).map(b => ({ ...b, label: t(b.value) })),
    [bucketCase, t]
  );

  // bucketCase가 바뀌었는데 현재 bucketType이 새 목록에 없으면 defaultYn 항목으로 리셋
  // (old setBucket의 기본값 선택 동작 재현, §A-3 — planScope와 무관하게 bucketCase 변경 시에만 동작).
  useEffect(() => {
    if (!useBucket) return;
    if (bucketOptions.some(b => b.value === value?.bucketType)) return;
    const def = bucketOptions.find(b => b.defaultYn === "Y") ?? bucketOptions[0];
    if (def) onChange({ bucketType: def.value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketOptions]);

  const isEntirePeriod = !!value?.isEntirePlanPeriod;

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <TextField
        size="small"
        label={t("FP_QUERY_RANGE")}
        value={t("FP_QUERY_RANGE")}
        inputRef={inputBoxRef}
        onClick={openPopover}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={openPopover}>
                {popoverOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        // minWidth가 아니라 width로 고정 — minWidth는 하한선이라 라벨/값("조회 구간")+드롭다운
        // 아이콘의 내용 폭이 더 넓으면 실제 렌더 폭이 그만큼 늘어난다(VersionSearchGroup과 동일
        // 사유, 2026-08-12 사용자 실측으로 발견).
        sx={{ ml: 1.5, width: 150 }}
      />
      <Popover
        anchorEl={anchorEl}
        open={popoverOpen}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2, width: 300 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <DatePicker
                label={t("STRT_DTTM")}
                format="yyyy-MM-dd"
                value={value?.startDate ?? null}
                maxDate={value?.endDate ?? undefined}
                disabled={isEntirePeriod}
                onAccept={(date) => onChange({ startDate: date })}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label={t("END_DTTM")}
                format="yyyy-MM-dd"
                value={value?.endDate ?? null}
                minDate={value?.startDate ?? undefined}
                disabled={isEntirePeriod}
                onAccept={(date) => onChange({ endDate: date })}
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
            {useBucket && (
              <FormControl size="small">
                <InputLabel id="period-search-bucket-label">{t("BUCKET")}</InputLabel>
                <Select
                  labelId="period-search-bucket-label"
                  label={t("BUCKET")}
                  value={value?.bucketType ?? ""}
                  onChange={(e) => onChange({ bucketType: e.target.value })}
                >
                  {bucketOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isEntirePeriod}
                  disabled={entireCheckDisabled}
                  onChange={(e) => onChange({ isEntirePlanPeriod: e.target.checked })}
                />
              }
              label={t("FP_ENTIRE_PERIOD")}
            />
          </Box>
        </LocalizationProvider>
      </Popover>
    </Box>
  );
};

export default PeriodSearchGroup;
